// Runtime color source for canvas / JS-drawn visuals. A CSS class on <html>
// does not repaint a <canvas>, so these components read the resolved palette
// here and re-init when the theme changes.

export type Theme = "dark" | "light";

export interface ThemePalette {
  theme: Theme;
  // RGB triplets "r, g, b" so callers can compose `rgba(${triplet} / a)`.
  accent: string;       // acid green
  neutral: string;      // chalk-2 family (links/ambient)
  pulse: string;        // brighter neural pulse/node tint
  bg: string;           // page background (for fade-to-bg gradient stops)
  // ASCII smoke ramps (low alpha -> high alpha), tuned per theme.
  smokeBase: string[];
  smokeWarm: string[];
}

const DARK: Omit<ThemePalette, "theme"> = {
  accent: "47, 107, 79",
  neutral: "167, 176, 164",
  pulse: "180, 200, 190",
  bg: "15, 20, 17",
  smokeBase: [
    "rgba(18,42,30,0)",
    "rgba(22,52,38,0.22)",
    "rgba(28,65,48,0.38)",
    "rgba(35,82,60,0.52)",
    "rgba(44,100,74,0.62)",
    "rgba(52,115,86,0.70)",
    "rgba(60,128,96,0.76)",
    "rgba(70,140,108,0.80)",
  ],
  smokeWarm: [
    "rgba(111,78,55,0.60)",
    "rgba(130,90,60,0.68)",
    "rgba(138,102,72,0.72)",
    "rgba(155,112,68,0.78)",
    "rgba(169,111,63,0.82)",
    "rgba(180,120,70,0.85)",
  ],
};

// Light = warm paper. Smoke must be DARKER than the bg to read (inverted from
// dark mode where it was lighter), with restrained alpha so it stays subtle.
const LIGHT: Omit<ThemePalette, "theme"> = {
  accent: "42, 95, 70",
  neutral: "120, 134, 122",
  pulse: "70, 110, 90",
  bg: "244, 242, 236",
  smokeBase: [
    "rgba(120,140,128,0)",
    "rgba(96,120,104,0.10)",
    "rgba(74,104,84,0.16)",
    "rgba(58,92,70,0.22)",
    "rgba(48,86,62,0.26)",
    "rgba(42,82,58,0.30)",
    "rgba(38,78,55,0.34)",
    "rgba(34,74,52,0.38)",
  ],
  smokeWarm: [
    "rgba(150,116,80,0.20)",
    "rgba(140,104,68,0.26)",
    "rgba(132,96,60,0.30)",
    "rgba(122,88,54,0.34)",
    "rgba(112,80,48,0.38)",
    "rgba(104,74,44,0.42)",
  ],
};

export function getPalette(theme: Theme): ThemePalette {
  return { theme, ...(theme === "light" ? LIGHT : DARK) };
}

// Read the current theme straight off the DOM so non-React canvas code can stay
// in sync without prop drilling.
export function currentTheme(): Theme {
  if (typeof document === "undefined") return "dark";
  return document.documentElement.classList.contains("light") ? "light" : "dark";
}
